import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse, HttpParams } from '@angular/common/http';
import { Observable, throwError, from, of } from 'rxjs';
import { catchError, switchMap, tap } from 'rxjs/operators';
import { AuthService } from './auth.service';
import { environment } from '../../environments/environment';
import { ApiResponse, ApiPaginatedResponse, ApiErrorCode } from './api.interface';
import { StaleUsersResponse } from 'shared_models';
import { User, UserRole, UpdateUserRequest, StaleUser, StaleUserDeletionResult, UserRequestPayload, UserRequestResponse, UserRequestSummary, UserRequestListResponse, UserRequestUpdatePayload, ApproveUserRequestResponse } from 'shared_models';

@Injectable({
  providedIn: 'root'
})
export class UserService {
  private apiUrl = environment.apiUrl;
  private userCache = new Map<string, User>();

  constructor(
    private http: HttpClient,
    private authService: AuthService
  ) {}

  /** API uses `startKey` (from prior response `nextKey`) for user/company/project list pagination. */
  private getPaginationParams(limit?: number, startKey?: string): HttpParams {
    let params = new HttpParams();
    if (limit) params = params.set('limit', limit.toString());
    if (startKey) params = params.set('startKey', startKey);
    return params;
  }

  private handleError(error: HttpErrorResponse) {
    console.error('API Error:', error);
    return throwError(() => error);
  }

  // Enhanced getUsers with query parameters support
  getUsers(options?: {
    role?: UserRole;
    companyID?: string;
    projectID?: string;
    limit?: number;
    /** Pagination cursor from previous response `nextKey` */
    startKey?: string;
    /** @deprecated Use startKey (maps to same query param) */
    lastEvaluatedKey?: string;
  }): Observable<ApiPaginatedResponse<User[]>> {
    return from(this.authService.getHeaders()).pipe(
      switchMap(headers => {
        const cursor = options?.startKey ?? options?.lastEvaluatedKey;
        let params = this.getPaginationParams(options?.limit, cursor);
        
        // Add query parameters if provided
        if (options?.role) {
          params = params.set('role', options.role);
        }
        if (options?.companyID) {
          params = params.set('companyID', options.companyID);
        }
        if (options?.projectID) {
          params = params.set('projectID', options.projectID);
        }
        
        const url = `${this.apiUrl}/users`;
        console.log('[API OUT] GET', url, { headers, params });
        return this.http.get<ApiPaginatedResponse<User[]>>(url, { headers, params }).pipe(
          tap(response => {
            console.log('[API IN] GET', url, response);
          })
        );
      }),
      catchError(this.handleError)
    );
  }

  getCurrentUser(): Observable<ApiResponse<User>> {
    return from(this.authService.getHeaders()).pipe(
      switchMap(headers => {
        const url = `${this.apiUrl}/users/me`;
        console.log('[API OUT] GET', url, { headers });
        return this.http.get<ApiResponse<User>>(url, { headers })
          .pipe(
            tap(response => {
              console.log('[API IN] GET', url, response);
            }),
            catchError(error => {
              console.error('API request error details:', {
                status: error.status,
                statusText: error.statusText,
                message: error.message,
                error: error.error,
                url: error.url,
                headers: error.headers,
                name: error.name
              });
              if (error.status === 0) {
                console.error('CORS error detected. The API server may not be configured to accept requests from this origin.');
              }
              return throwError(() => error);
            })
          );
      }),
      catchError(this.handleError)
    );
  }

  getUser(userId: string): Observable<ApiResponse<User>> {
    // Check cache first
    if (this.userCache.has(userId)) {
      const cachedUser = this.userCache.get(userId)!;
      console.log('[CACHE HIT] User', userId, cachedUser);
      return of({ data: cachedUser, message: 'User loaded from cache', code: ApiErrorCode.Success });
    }

    return from(this.authService.getHeaders()).pipe(
      switchMap(headers => {
        const url = `${this.apiUrl}/users/${userId}`;
        console.log('[API OUT] GET', url, { headers });
        return this.http.get<ApiResponse<User>>(url, { headers }).pipe(
          tap(response => {
            console.log('[API IN] GET', url, response);
            // Cache the user data
            if (response.data) {
              this.userCache.set(userId, response.data);
            }
          })
        );
      }),
      catchError(this.handleError)
    );
  }

  createUser(userData: Partial<User> & { temporaryPassword: string }): Observable<ApiResponse<User>> {
    return from(this.authService.getHeaders()).pipe(
      switchMap(headers => {
        const url = `${this.apiUrl}/users`;
        console.log('[API OUT] POST', url, { headers, body: userData });
        return this.http.post<ApiResponse<User>>(url, userData, { headers }).pipe(
          tap(response => {
            console.log('[API IN] POST', url, response);
          })
        );
      }),
      catchError(this.handleError)
    );
  }

  /**
   * Creates a new user request for admin review. POST /users/requests
   */
  createUserRequest(payload: UserRequestPayload): Observable<ApiResponse<UserRequestResponse>> {
    return from(this.authService.getHeaders()).pipe(
      switchMap(headers => {
        const url = `${this.apiUrl}/users/requests`;
        console.log('[API OUT] POST', url, { headers, body: payload });
        return this.http.post<ApiResponse<UserRequestResponse>>(url, payload, { headers }).pipe(
          tap(response => {
            console.log('[API IN] POST', url, response);
          })
        );
      }),
      catchError(this.handleError)
    );
  }

  /**
   * List user requests with optional filters and pagination. GET /users/requests
   */
  listUserRequests(options?: { limit?: number; startKey?: string; companyID?: string; status?: string }): Observable<ApiResponse<UserRequestListResponse>> {
    return from(this.authService.getHeaders()).pipe(
      switchMap(headers => {
        let params = new HttpParams();
        if (options?.limit) params = params.set('limit', options.limit.toString());
        if (options?.startKey) params = params.set('startKey', options.startKey);
        if (options?.companyID) params = params.set('companyID', options.companyID);
        if (options?.status) params = params.set('status', options.status);
        const url = `${this.apiUrl}/users/requests`;
        console.log('[API OUT] GET', url, { headers, params: options });
        return this.http.get<ApiResponse<UserRequestListResponse>>(url, { headers, params }).pipe(
          tap(response => console.log('[API IN] GET', url, response))
        );
      }),
      catchError(this.handleError)
    );
  }

  /**
   * Update a user request. PUT /users/requests/{requestId}
   */
  updateUserRequest(requestId: string, payload: UserRequestUpdatePayload): Observable<ApiResponse<{ message: string; requestId: string }>> {
    return from(this.authService.getHeaders()).pipe(
      switchMap(headers => {
        const url = `${this.apiUrl}/users/requests/${requestId}`;
        console.log('[API OUT] PUT', url, { headers, body: payload });
        return this.http.put<ApiResponse<{ message: string; requestId: string }>>(url, payload, { headers }).pipe(
          tap(response => console.log('[API IN] PUT', url, response))
        );
      }),
      catchError(this.handleError)
    );
  }

  /**
   * Delete a user request. DELETE /users/requests/{requestId}
   */
  deleteUserRequest(requestId: string): Observable<ApiResponse<{ message: string; requestId: string }>> {
    return from(this.authService.getHeaders()).pipe(
      switchMap(headers => {
        const url = `${this.apiUrl}/users/requests/${requestId}`;
        console.log('[API OUT] DELETE', url, { headers });
        return this.http.delete<ApiResponse<{ message: string; requestId: string }>>(url, { headers }).pipe(
          tap(response => console.log('[API IN] DELETE', url, response))
        );
      }),
      catchError(this.handleError)
    );
  }

  /**
   * Approve a user request and create the user in Cognito. POST /users/requests/{requestId}/approve (GlobalAdmin only)
   */
  approveUserRequest(requestId: string): Observable<ApiResponse<ApproveUserRequestResponse>> {
    return from(this.authService.getHeaders()).pipe(
      switchMap(headers => {
        const url = `${this.apiUrl}/users/requests/${requestId}/approve`;
        console.log('[API OUT] POST', url, { headers });
        return this.http.post<ApiResponse<ApproveUserRequestResponse>>(url, {}, { headers }).pipe(
          tap(response => console.log('[API IN] POST', url, response))
        );
      }),
      catchError(this.handleError)
    );
  }

  getProjectUsers(projectId: string, limit?: number, startKey?: string): Observable<ApiPaginatedResponse<User[]>> {
    return from(this.authService.getHeaders()).pipe(
      switchMap(headers => {
        const params = this.getPaginationParams(limit, startKey);
        const url = `${this.apiUrl}/projects/${projectId}/users`;
        console.log('[API OUT] GET', url, { headers, params });
        return this.http.get<ApiPaginatedResponse<User[]>>(url, { headers, params }).pipe(
          tap(response => {
            console.log('[API IN] GET', url, response);
          })
        );
      }),
      catchError(this.handleError)
    );
  }

  getCompanyUsers(companyId: string, limit?: number, startKey?: string): Observable<ApiPaginatedResponse<User[]>> {
    return from(this.authService.getHeaders()).pipe(
      switchMap(headers => {
        const params = this.getPaginationParams(limit, startKey);
        const url = `${this.apiUrl}/companies/${companyId}/users`;
        console.log('[API OUT] GET', url, { headers, params });
        return this.http.get<ApiPaginatedResponse<User[]>>(url, { headers, params }).pipe(
          tap(response => {
            console.log('[API IN] GET', url, response);
          })
        );
      }),
      catchError(this.handleError)
    );
  }

  /**
   * Update the current user's profile information
   */
  updateCurrentUser(userData: UpdateUserRequest): Observable<ApiResponse<User>> {
    return from(this.authService.getHeaders()).pipe(
      switchMap(headers => {
        const url = `${this.apiUrl}/users/me`;
        console.log('[API OUT] PUT', url, { headers, body: userData });
        return this.http.put<ApiResponse<User>>(url, userData, { headers }).pipe(
          tap(response => {
            console.log('[API IN] PUT', url, response);
          })
        );
      }),
      catchError(this.handleError)
    );
  }

  /**
   * Update a user's information
   */
  updateUser(userData: Partial<User> & { id: string }): Observable<ApiResponse<User>> {
    return from(this.authService.getHeaders()).pipe(
      switchMap(headers => {
        const url = `${this.apiUrl}/users/${userData.id}`;
        console.log('[API OUT] PUT', url, { headers, body: userData });
        return this.http.put<ApiResponse<User>>(url, userData, { headers }).pipe(
          tap(response => {
            console.log('[API IN] PUT', url, response);
          })
        );
      }),
      catchError(this.handleError)
    );
  }

  /**
   * Delete a user by userID
   */
  deleteUser(userID: string): Observable<ApiResponse<any>> {
    return from(this.authService.getHeaders()).pipe(
      switchMap(headers => {
        const url = `${this.apiUrl}/users/${userID}`;
        console.log('[API OUT] DELETE', url, { headers });
        return this.http.delete<ApiResponse<any>>(url, { headers }).pipe(
          tap(response => {
            console.log('[API IN] DELETE', url, response);
          })
        );
      }),
      catchError(this.handleError)
    );
  }

  /**
   * Assign a role to a user (GlobalAdmin only)
   */
  assignRoleToUser(userID: string, role: UserRole): Observable<ApiResponse<any>> {
    return from(this.authService.getHeaders()).pipe(
      switchMap(headers => {
        const url = `${this.apiUrl}/users/${userID}/role`;
        const body = { role };
        console.log('[API OUT] PUT', url, { headers, body });
        return this.http.put<ApiResponse<any>>(url, body, { headers }).pipe(
          tap(response => {
            console.log('[API IN] PUT', url, response);
          })
        );
      }),
      catchError(this.handleError)
    );
  }

  /**
   * List users with optional filters (role, companyID, projectID, etc.)
   * Example usage: listUsers({ projectID: 'project-123' })
   */
  /**
   * List users via GET /users with optional role, companyID, projectID, limit, startKey.
   * Uses a single endpoint so project and company filters can be combined (per API).
   */
  listUsers(options?: {
    role?: string;
    companyID?: string;
    projectID?: string;
    limit?: number;
    startKey?: string;
    /** @deprecated Use startKey */
    lastEvaluatedKey?: string;
  }): Observable<ApiPaginatedResponse<User[]>> {
    return from(this.authService.getHeaders()).pipe(
      switchMap(headers => {
        let httpParams = new HttpParams();
        if (options?.role) httpParams = httpParams.set('role', options.role);
        if (options?.companyID) httpParams = httpParams.set('companyID', options.companyID);
        if (options?.projectID) httpParams = httpParams.set('projectID', options.projectID);
        if (options?.limit) httpParams = httpParams.set('limit', options.limit.toString());
        const cursor = options?.startKey ?? options?.lastEvaluatedKey;
        if (cursor) httpParams = httpParams.set('startKey', cursor);
        const url = `${this.apiUrl}/users`;
        console.log('[API OUT] GET', url, { headers, params: httpParams });
        return this.http.get<ApiPaginatedResponse<User[]>>(url, { headers, params: httpParams }).pipe(
          tap(response => {
            console.log('[API IN] GET', url, response);
          })
        );
      }),
      catchError(this.handleError)
    );
  }

  /**
   * List stale user invites with optional project assignment details
   * Stale users are defined as users in FORCE_CHANGE_PASSWORD status where createdAt equals lastModifiedAt (never logged in)
   */
  listStaleInvites(options?: {
    includeAssignments?: boolean;
    limit?: number;
    paginationToken?: string;
  }): Observable<ApiResponse<StaleUsersResponse>> {
    return from(this.authService.getHeaders()).pipe(
      switchMap(headers => {
        let params = new HttpParams();
        if (options?.includeAssignments) {
          params = params.set('includeAssignments', 'true');
        }
        if (options?.limit) {
          params = params.set('limit', options.limit.toString());
        }
        if (options?.paginationToken) {
          params = params.set('paginationToken', options.paginationToken);
        }

        const url = `${this.apiUrl}/users/stale-invites`;
        console.log('[API OUT] GET', url, { headers, params });
        return this.http.get<ApiResponse<StaleUsersResponse>>(url, { headers, params }).pipe(
          tap(response => {
            console.log('[API IN] GET', url, response);
          })
        );
      }),
      catchError(this.handleError)
    );
  }

  /**
   * Delete a specific stale user and all their project assignments
   * Only deletes users that are truly stale (never logged in)
   */
  deleteStaleUser(userID: string): Observable<ApiResponse<StaleUserDeletionResult>> {
    return from(this.authService.getHeaders()).pipe(
      switchMap(headers => {
        const url = `${this.apiUrl}/users/stale-invites/${userID}`;
        console.log('[API OUT] DELETE', url, { headers });
        return this.http.delete<ApiResponse<StaleUserDeletionResult>>(url, { headers }).pipe(
          tap(response => {
            console.log('[API IN] DELETE', url, response);
          })
        );
      }),
      catchError(this.handleError)
    );
  }
} 